import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiBookOpen,
  FiPlus,
  FiSearch,
  FiUser,
  FiPhone,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiTrash2,
  FiEdit2,
  FiRefreshCw,
  FiX,
  FiDollarSign,
  FiUserPlus,
} from "react-icons/fi";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import ToolsHeader from "./ToolsHeader";
import {
  getAllKhatabooks,
  createKhatabook,
  getKhatabookSummary,
  listKhatabookCustomers,
  addKhatabookCustomer,
  editKhatabookCustomer,
  deleteKhatabookCustomer,
  getCustomerSummary,
  searchCustomerTransactions,
  addCustomerTransaction,
  editCustomerTransaction,
  deleteCustomerTransaction,
} from "../../api/tools/toolsApi";
import { ToolsSkeleton, ToolsLoadError } from "./ToolsState";
import { CUSTOMER_TOOLS_BASE, KHATABOOK_HOME, useFullPageForms } from "./forms/toolFormHelpers";
import {
  TX_MODES,
  bookLabel,
  customerName,
  customerPhone,
  dateInputValue,
  formatTxDate,
  inr,
  isCredit,
  todayDate,
  toApiDateTime,
} from "./forms/khatabookModel";
import "./Tools.css";

const EMPTY_CUSTOMER = { id: null, name: "", phone: "" };
const emptyTx = (type = "CREDIT") => ({
  id: null,
  type,
  amount: "",
  mode: "CASH",
  note: "",
  transactionDate: todayDate(),
  original: null,
});

// The API answers 200 with statusType FAILURE for business errors
const assertSuccess = (res, fallback) => {
  if (res?.statusType && res.statusType !== "SUCCESS") throw new Error(res.text || fallback);
  return res;
};
const errorText = (err, fallback) => err?.response?.data?.text || err?.response?.data?.message || err?.message || fallback;

const KhatabookTool = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Under /customer/tools the add/edit forms are full pages; elsewhere they stay popups
  const fullPageForms = useFullPageForms(CUSTOMER_TOOLS_BASE);
  const preferredBookId = searchParams.get("book");
  const preferredCustomerId = searchParams.get("customer");
  const reopenedCustomer = useRef(false);

  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  // Khatabooks (the backend gives every user a HOME and an OFFICE book)
  const [khatabooks, setKhatabooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookSummary, setBookSummary] = useState(null);
  const [loadingBooks, setLoadingBooks] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [settingUp, setSettingUp] = useState(false);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  // Customer transactions / drawer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Popups (only used outside /customer/tools)
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER);
  const [submittingCustomer, setSubmittingCustomer] = useState(false);

  const [showTxModal, setShowTxModal] = useState(false);
  const [txForm, setTxForm] = useState(emptyTx());
  const [submittingTx, setSubmittingTx] = useState(false);

  // 1. Fetch the user's Khatabooks
  const fetchKhatabooks = useCallback(async () => {
    if (!token) return;
    setLoadingBooks(true);
    setLoadError(false);
    try {
      const data = await getAllKhatabooks(token);
      assertSuccess(data, "Failed to load Khatabooks");
      const list = data?.khatabooks || [];
      setKhatabooks(list);

      if (list.length > 0 && !selectedBook) {
        setSelectedBook(list.find((b) => String(b.id) === preferredBookId) || list[0]);
      } else if (list.length === 0) {
        setSelectedBook(null);
      }
    } catch (err) {
      console.error("Error fetching khatabooks:", err);
      setLoadError(true);
      toast.error(errorText(err, "Failed to load Khatabooks"));
    } finally {
      setLoadingBooks(false);
    }
  }, [token, selectedBook, preferredBookId]);

  useEffect(() => {
    fetchKhatabooks();
  }, [fetchKhatabooks]);

  // 2. Fetch Book Summary & Customers when selectedBook changes
  const fetchBookDetails = useCallback(async (bookId) => {
    if (!token || !bookId) return;
    setLoadingCustomers(true);
    try {
      const [summaryRes, customersRes] = await Promise.allSettled([
        getKhatabookSummary(token, bookId),
        listKhatabookCustomers(token, bookId, 1, 1000),
      ]);

      setBookSummary(summaryRes.status === "fulfilled" ? summaryRes.value : null);
      setCustomers(customersRes.status === "fulfilled" ? customersRes.value?.khataBookCustomers || [] : []);
    } catch (err) {
      console.error("Error fetching book details:", err);
    } finally {
      setLoadingCustomers(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedBook?.id) {
      fetchBookDetails(selectedBook.id);
    }
  }, [selectedBook, fetchBookDetails]);

  // 3. Set up the Khatabooks (the backend creates HOME and OFFICE once; no name is taken)
  const handleSetupKhatabook = async () => {
    setSettingUp(true);
    try {
      assertSuccess(await createKhatabook(token), "Failed to set up Khatabook");
      toast.success("Khatabook is ready!");
      await fetchKhatabooks();
    } catch (err) {
      console.error("Error creating khatabook:", err);
      toast.error(errorText(err, "Failed to set up Khatabook"));
    } finally {
      setSettingUp(false);
    }
  };

  // 4. Open Customer Details & Transactions
  const handleOpenCustomer = useCallback(async (cust) => {
    setSelectedCustomer(cust);
    setLoadingTransactions(true);
    try {
      const [sumRes, txRes] = await Promise.allSettled([
        getCustomerSummary(token, cust.id),
        searchCustomerTransactions(token, cust.id, 1, 100),
      ]);
      setCustomerSummary(sumRes.status === "fulfilled" ? sumRes.value : null);
      setTransactions(txRes.status === "fulfilled" ? txRes.value?.khataTransactions || [] : []);
    } catch (err) {
      console.error("Error loading customer transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [token]);

  // Coming back from a transaction form: reopen that customer's ledger
  useEffect(() => {
    if (!preferredCustomerId || reopenedCustomer.current || customers.length === 0) return;
    const match = customers.find((c) => String(c.id) === preferredCustomerId);
    if (match) {
      reopenedCustomer.current = true;
      handleOpenCustomer(match);
    }
  }, [customers, preferredCustomerId, handleOpenCustomer]);

  // Open a form: a full page under /customer/tools, otherwise the popup
  const openCustomerForm = (customer = null) => {
    if (fullPageForms) {
      if (!selectedBook?.id) return;
      navigate(
        customer
          ? `${KHATABOOK_HOME}/${selectedBook.id}/customers/${customer.id}/edit`
          : `${KHATABOOK_HOME}/${selectedBook.id}/customers/new`,
        customer ? { state: { customer } } : undefined
      );
      return;
    }
    setCustomerForm(
      customer ? { id: customer.id, name: customerName(customer), phone: customerPhone(customer) } : EMPTY_CUSTOMER
    );
    setShowCustomerModal(true);
  };

  const openTransactionForm = ({ tx = null, type = "CREDIT" } = {}) => {
    if (fullPageForms) {
      if (!selectedBook?.id || !selectedCustomer?.id) return;
      const base = `${KHATABOOK_HOME}/${selectedBook.id}/customers/${selectedCustomer.id}/transactions`;
      navigate(tx ? `${base}/${tx.id}/edit` : `${base}/new?type=${type}`, tx ? { state: { transaction: tx } } : undefined);
      return;
    }
    setTxForm(
      tx
        ? {
            id: tx.id,
            type: isCredit(tx) ? "CREDIT" : "DEBIT",
            amount: tx.amount,
            mode: tx.mode || "CASH",
            note: tx.note || "",
            transactionDate: dateInputValue(tx.transactionDate),
            original: tx.transactionDate,
          }
        : emptyTx(type)
    );
    setShowTxModal(true);
  };

  // 5. Add or Edit Customer (popup)
  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (!selectedBook?.id) {
      toast.warning("Please select a Khatabook first");
      return;
    }
    if (!customerForm.name.trim() || !customerForm.phone.trim()) {
      toast.warning("Please fill in both name and mobile number");
      return;
    }
    setSubmittingCustomer(true);
    try {
      const body = { name: customerForm.name, phone: customerForm.phone };
      const res = customerForm.id
        ? await editKhatabookCustomer(token, customerForm.id, body)
        : await addKhatabookCustomer(token, selectedBook.id, body);
      assertSuccess(res, "Failed to save customer");
      toast.success(res?.text || (customerForm.id ? "Customer updated successfully" : "Customer added successfully"));
      setShowCustomerModal(false);
      setCustomerForm(EMPTY_CUSTOMER);
      fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error saving customer:", err);
      toast.error(errorText(err, "Failed to save customer"));
    } finally {
      setSubmittingCustomer(false);
    }
  };

  // 6. Delete Customer
  const handleDeleteCustomer = async (customerId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      assertSuccess(await deleteKhatabookCustomer(token, customerId), "Failed to delete customer");
      toast.success("Customer deleted");
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(null);
      }
      fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error deleting customer:", err);
      toast.error(errorText(err, "Failed to delete customer"));
    }
  };

  // 7. Add or Edit Transaction (popup)
  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    if (!selectedCustomer?.id) return;
    if (!txForm.amount || Number(txForm.amount) <= 0) {
      toast.warning("Please enter a valid amount");
      return;
    }
    if (!txForm.id && txForm.transactionDate < todayDate()) {
      toast.warning("Transaction date cannot be in the past");
      return;
    }
    setSubmittingTx(true);
    try {
      const body = {
        amount: Number(txForm.amount),
        type: txForm.type,
        note: txForm.note,
        transactionDate: toApiDateTime(txForm.transactionDate, txForm.original),
        khataBookMode: txForm.mode,
      };
      const res = txForm.id
        ? await editCustomerTransaction(token, txForm.id, body)
        : await addCustomerTransaction(token, selectedCustomer.id, body);
      assertSuccess(res, "Failed to save transaction");
      toast.success(res?.text || (txForm.id ? "Transaction updated" : "Transaction recorded"));
      setShowTxModal(false);
      setTxForm(emptyTx());
      handleOpenCustomer(selectedCustomer);
      if (selectedBook?.id) fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error saving transaction:", err);
      toast.error(errorText(err, "Failed to save transaction"));
    } finally {
      setSubmittingTx(false);
    }
  };

  // 8. Delete Transaction
  const handleDeleteTransaction = async (txId) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;
    try {
      assertSuccess(await deleteCustomerTransaction(token, txId), "Failed to delete transaction");
      toast.success("Transaction deleted");
      handleOpenCustomer(selectedCustomer);
      if (selectedBook?.id) fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error deleting transaction:", err);
      toast.error(errorText(err, "Failed to delete transaction"));
    }
  };

  // Filtered customers
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(
      (c) => customerName(c).toLowerCase().includes(q) || customerPhone(c).includes(q)
    );
  }, [customers, customerSearch]);

  // Book totals come straight from the summary endpoint
  const totalCredit = Number(bookSummary?.totalCredit || 0);
  const totalDebit = Number(bookSummary?.totalDebit || 0);
  const totalBalance = Number(bookSummary?.totalBalance || 0);

  const ledgerCard = { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px" };
  const avatar = (size, text) => ({
    width: size,
    height: size,
    borderRadius: "50%",
    background: "#eff6ff",
    color: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: text,
  });

  return (
    <div className="tools-container">
      <ToolsHeader
        title="Khatabook Tool"
        subtitle="Manage customer credit/debit accounts, track balances, and record transactions."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              className="tools-btn tools-btn--secondary"
              onClick={fetchKhatabooks}
              disabled={loadingBooks}
            >
              <FiRefreshCw className={loadingBooks ? "spin" : ""} /> Refresh
            </button>
            {selectedBook ? (
              <button className="tools-btn tools-btn--primary" onClick={() => openCustomerForm()}>
                <FiUserPlus /> Add Customer
              </button>
            ) : (
              !loadingBooks &&
              !loadError && (
                <button className="tools-btn tools-btn--primary" onClick={handleSetupKhatabook} disabled={settingUp}>
                  <FiPlus /> {settingUp ? "Setting up..." : "Set Up Khatabook"}
                </button>
              )
            )}
          </div>
        }
      />

      {loadError && <ToolsLoadError text="Couldn't load your khatabooks." onRetry={fetchKhatabooks} />}

      {/* Khatabook Selector Bar */}
      <div className="tools-action-bar">
        <div className="d-flex align-items-center gap-3 flex-wrap flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <FiBookOpen className="text-primary" size={20} />
            <span className="fw-bold text-dark">Active Book:</span>
          </div>
          {loadingBooks ? (
            <span className="text-muted small">Loading books...</span>
          ) : khatabooks.length === 0 ? (
            <span className="text-muted small">No Khatabook yet. Set it up to get started.</span>
          ) : (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {khatabooks.map((book) => (
                <button
                  key={book.id}
                  onClick={() => setSelectedBook(book)}
                  className={`tools-btn tools-btn--sm ${
                    selectedBook?.id === book.id ? "tools-btn--primary" : "tools-btn--secondary"
                  }`}
                >
                  {bookLabel(book)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Book Summary Metrics */}
      {selectedBook && (
        <div className="tools-stats-grid">
          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--green">
              <FiArrowDownLeft />
            </div>
            <div>
              <div className="tools-stat-card__label">Total Credit</div>
              <div className="tools-stat-card__value text-success">{inr(totalCredit)}</div>
            </div>
          </div>

          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--red">
              <FiArrowUpRight />
            </div>
            <div>
              <div className="tools-stat-card__label">Total Debit</div>
              <div className="tools-stat-card__value text-danger">{inr(totalDebit)}</div>
            </div>
          </div>

          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--blue">
              <FiDollarSign />
            </div>
            <div>
              <div className="tools-stat-card__label">Total Balance</div>
              <div className={`tools-stat-card__value ${totalBalance >= 0 ? "text-success" : "text-danger"}`}>
                {totalBalance < 0 ? "-" : ""}
                {inr(Math.abs(totalBalance))}
              </div>
            </div>
          </div>

          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--purple">
              <FiUser />
            </div>
            <div>
              <div className="tools-stat-card__label">Total Customers</div>
              <div className="tools-stat-card__value text-dark">{customers.length}</div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Search & Filter */}
      {selectedBook && (
        <div className="tools-action-bar">
          <div className="tools-search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search customers by name or mobile number..."
              aria-label="Search customers"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </div>
          <span className="text-muted small">
            Showing {filteredCustomers.length} of {customers.length} customer(s)
          </span>
        </div>
      )}

      {/* Customers Table / List */}
      {!selectedBook ? (
        !loadingBooks &&
        !loadError && (
          <div className="tools-empty-state">
            <div className="tools-empty-state__icon">
              <FiBookOpen />
            </div>
            <h4>Set Up Your Khatabook</h4>
            <p>We will create your Home and Office Khatabooks so you can start adding customers.</p>
            <button className="tools-btn tools-btn--primary" onClick={handleSetupKhatabook} disabled={settingUp}>
              <FiPlus /> {settingUp ? "Setting up..." : "Set Up Khatabook"}
            </button>
          </div>
        )
      ) : loadingCustomers ? (
        <ToolsSkeleton label="Loading customers" />
      ) : filteredCustomers.length === 0 ? (
        <div className="tools-empty-state">
          <div className="tools-empty-state__icon">
            <FiUser />
          </div>
          <h4>{customers.length === 0 ? "No Customers in this Khatabook" : "No customers match your search"}</h4>
          <p>
            {customers.length === 0
              ? "Add customers to start recording credit and debit entries."
              : "Try a different name or mobile number."}
          </p>
          {customers.length === 0 && (
            <button className="tools-btn tools-btn--primary" onClick={() => openCustomerForm()}>
              <FiUserPlus /> Add Customer
            </button>
          )}
        </div>
      ) : (
        <div className="tools-table-card">
          <div className="tools-table-responsive">
            <table className="tools-table tools-table--khatabook">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Mobile Number</th>
                  <th>Balance</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((cust) => {
                  const balance = Number(cust.totalBalance || 0);
                  return (
                    <tr key={cust.id} style={{ cursor: "pointer" }} onClick={() => handleOpenCustomer(cust)}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div style={avatar(36)}>{customerName(cust).charAt(0).toUpperCase() || "C"}</div>
                          <div>
                            <div className="fw-bold text-dark">{customerName(cust)}</div>
                            <div className="text-muted small">ID: #{cust.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-secondary d-flex align-items-center gap-1">
                          <FiPhone size={13} /> {customerPhone(cust) || "—"}
                        </span>
                      </td>
                      <td>
                        <span className={`fw-bold ${balance >= 0 ? "text-success" : "text-danger"}`}>
                          {balance < 0 ? "-" : ""}
                          {inr(Math.abs(balance))}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <button className="tools-btn tools-btn--sm tools-btn--secondary" onClick={() => handleOpenCustomer(cust)}>
                            View Passbook
                          </button>
                          <button
                            className="tools-btn--icon"
                            title="Edit Customer"
                            aria-label="Edit customer"
                            onClick={() => openCustomerForm(cust)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="tools-btn--icon tools-btn--icon-danger"
                            title="Delete Customer"
                            aria-label="Delete customer"
                            onClick={(e) => handleDeleteCustomer(cust.id, e)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customer Passbook Drawer / Side Panel */}
      {selectedCustomer && (
        <div className="tools-drawer-overlay" onClick={() => setSelectedCustomer(null)}>
          <div className="tools-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="tools-drawer__header">
              <div className="d-flex align-items-center gap-3">
                <div style={avatar(44, "1.1rem")}>{customerName(selectedCustomer).charAt(0).toUpperCase()}</div>
                <div>
                  <h4 className="fw-bold mb-0 text-dark">{customerName(selectedCustomer)}</h4>
                  <div className="text-muted small d-flex align-items-center gap-1">
                    <FiPhone size={13} /> {customerPhone(selectedCustomer)}
                  </div>
                </div>
              </div>
              <button className="tools-btn--icon" aria-label="Close passbook" onClick={() => setSelectedCustomer(null)}>
                <FiX size={20} />
              </button>
            </div>

            <div className="tools-drawer__body">
              {/* Customer Summary Cards */}
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div style={ledgerCard}>
                    <div className="text-muted small fw-semibold">Total Credit</div>
                    <div className="fw-bold text-success fs-6">{inr(customerSummary?.totalCredit)}</div>
                  </div>
                </div>
                <div className="col-4">
                  <div style={ledgerCard}>
                    <div className="text-muted small fw-semibold">Total Debit</div>
                    <div className="fw-bold text-danger fs-6">{inr(customerSummary?.totalDebit)}</div>
                  </div>
                </div>
                <div className="col-4">
                  <div style={ledgerCard}>
                    <div className="text-muted small fw-semibold">Balance</div>
                    <div className="fw-bold text-dark fs-6">{inr(customerSummary?.totalBalance)}</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons for Customer Transaction */}
              <div className="d-flex gap-2 mb-4">
                <button className="tools-btn tools-btn--success flex-grow-1" onClick={() => openTransactionForm({ type: "CREDIT" })}>
                  <FiArrowDownLeft /> Credit (Money In)
                </button>
                <button className="tools-btn tools-btn--danger flex-grow-1" onClick={() => openTransactionForm({ type: "DEBIT" })}>
                  <FiArrowUpRight /> Debit (Money Out)
                </button>
              </div>

              {/* Transactions History Header */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold text-dark mb-0">Transaction History</h6>
                <button
                  className="tools-btn--icon"
                  onClick={() => handleOpenCustomer(selectedCustomer)}
                  title="Refresh Transactions"
                  aria-label="Refresh transactions"
                >
                  <FiRefreshCw size={14} className={loadingTransactions ? "spin" : ""} />
                </button>
              </div>

              {/* Transactions List */}
              {loadingTransactions ? (
                <div className="py-2">
                  <ToolsSkeleton rows={3} label="Loading transactions" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="tools-empty-state py-4">
                  <p className="mb-0">No transactions recorded yet.</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {transactions.map((tx) => {
                    const credit = isCredit(tx);
                    return (
                      <div
                        key={tx.id}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "12px 14px",
                          background: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <span className={`tools-badge ${credit ? "tools-badge--got" : "tools-badge--gave"}`}>
                              {credit ? "Credit" : "Debit"}
                            </span>
                            {tx.mode && <span className="text-muted small">{tx.mode}</span>}
                            <span className="text-muted small">{formatTxDate(tx.transactionDate)}</span>
                          </div>
                          <div className="text-secondary small">{tx.note || "No notes"}</div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <span className={`fw-bold fs-6 ${credit ? "text-success" : "text-danger"}`}>
                            {credit ? "+" : "-"}
                            {inr(tx.amount)}
                          </span>
                          <button
                            className="tools-btn--icon"
                            title="Edit"
                            aria-label="Edit transaction"
                            onClick={() => openTransactionForm({ tx })}
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            className="tools-btn--icon tools-btn--icon-danger"
                            title="Delete"
                            aria-label="Delete transaction"
                            onClick={() => handleDeleteTransaction(tx.id)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Popup: Add/Edit Customer (outside /customer/tools) */}
      <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {customerForm.id ? "Edit Customer" : "Add Customer to Khatabook"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveCustomer}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Customer Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. John Doe"
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Mobile Number *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="e.g. 9876543210"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowCustomerModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingCustomer}>
              {submittingCustomer ? "Saving..." : customerForm.id ? "Update" : "Add Customer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Popup: Add/Edit Transaction (outside /customer/tools) */}
      <Modal show={showTxModal} onHide={() => setShowTxModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">{txForm.id ? "Edit Transaction" : "Record Transaction"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveTransaction}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Transaction Type *</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={txForm.type === "CREDIT" ? "success" : "outline-success"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setTxForm({ ...txForm, type: "CREDIT" })}
                >
                  <FiArrowDownLeft /> Credit (Money In)
                </Button>
                <Button
                  variant={txForm.type === "DEBIT" ? "danger" : "outline-danger"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setTxForm({ ...txForm, type: "DEBIT" })}
                >
                  <FiArrowUpRight /> Debit (Money Out)
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Amount (₹) *</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 5000"
                value={txForm.amount}
                onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })}
                required
                min="1"
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Payment Mode *</Form.Label>
              <div className="d-flex gap-2">
                {TX_MODES.map((m) => (
                  <Button
                    key={m.value}
                    variant={txForm.mode === m.value ? "primary" : "outline-primary"}
                    className="flex-grow-1"
                    type="button"
                    onClick={() => setTxForm({ ...txForm, mode: m.value })}
                  >
                    {m.label}
                  </Button>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Date *</Form.Label>
              <Form.Control
                type="date"
                value={txForm.transactionDate}
                min={txForm.id ? undefined : todayDate()}
                onChange={(e) => setTxForm({ ...txForm, transactionDate: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="e.g. Materials purchased, Partial payment, etc."
                value={txForm.note}
                onChange={(e) => setTxForm({ ...txForm, note: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowTxModal(false)}>
              Cancel
            </Button>
            <Button variant={txForm.type === "CREDIT" ? "success" : "danger"} type="submit" disabled={submittingTx}>
              {submittingTx ? <Spinner animation="border" size="sm" /> : txForm.id ? "Update Entry" : "Save Entry"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default KhatabookTool;
